import React, { useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Avatar,
    Chip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    List,
    ListItem,
    ListItemText,
    Divider
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { adminApi } from '../../services/api';

const CodeOwnerDetails = ({ ownerData, onDeleted }) => {
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletionInfo, setDeletionInfo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleDeleteClick = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await adminApi.getCodeOwnerDeletionInfo(ownerData.owner_id);
            setDeletionInfo(response.data);
            setDeleteDialogOpen(true);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmDelete = async () => {
        try {
            setLoading(true);
            setError(null);
            await adminApi.deleteCodeOwner(ownerData.owner_id);
            setDeleteDialogOpen(false);
            if (onDeleted) onDeleted();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (!ownerData) return null;

    return (
        <Box>
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, mb: 2 }}>
                        <Typography variant="h6" gutterBottom>Basic Information</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box>
                                <Typography variant="subtitle2" color="textSecondary">Email</Typography>
                                <Typography>{ownerData.email}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="textSecondary">Owner ID</Typography>
                                <Typography>{ownerData.owner_id}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="textSecondary">Status & Onboarding</Typography>
                                <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                    <Chip 
                                        label={ownerData.registration_completed ? "Registration: Completed" : "Registration: Pending"}
                                        color={ownerData.registration_completed ? "success" : "default"}
                                        size="small"
                                    />
                                    <Chip 
                                        label={ownerData.is_email_verified ? "Email Verified" : "Email Unverified"}
                                        color={ownerData.is_email_verified ? "success" : "warning"}
                                        size="small"
                                    />
                                </Box>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="textSecondary">Created</Typography>
                                <Typography>{new Date(ownerData.created_at).toLocaleString()}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="textSecondary">Last Login</Typography>
                                <Typography>
                                    {ownerData.last_login ? new Date(ownerData.last_login).toLocaleString() : 'Never'}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="textSecondary">Free Codes</Typography>
                                <Typography>{ownerData.free_codes || 0}</Typography>
                            </Box>
                            <Box sx={{ mt: 2 }}>
                                <Button
                                    variant="contained"
                                    color="error"
                                    startIcon={<DeleteIcon />}
                                    onClick={handleDeleteClick}
                                    disabled={loading}
                                >
                                    Delete Code Owner
                                </Button>
                                {error && (
                                    <Typography color="error" sx={{ mt: 1 }}>
                                        {error}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={12}>
                    <Paper sx={{ p: 2, mb: 2 }}>
                        <Typography variant="h6" gutterBottom>Associated Codes</Typography>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Code</TableCell>
                                        <TableCell>Image</TableCell>
                                        <TableCell>Web Address</TableCell>
                                        <TableCell>Valid From</TableCell>
                                        <TableCell>Valid Until</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {ownerData.codes?.map((code) => (
                                        <TableRow key={code.code_id}>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography>{code.code_value}</Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        (ID: {code.code_id})
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                {code.image_url ? (
                                                    <Avatar
                                                        src={code.image_url}
                                                        alt={code.code_value}
                                                        variant="rounded"
                                                        sx={{ width: 40, height: 40 }}
                                                    />
                                                ) : (
                                                    <Box 
                                                        sx={{ 
                                                            width: 40, 
                                                            height: 40, 
                                                            bgcolor: 'grey.100',
                                                            borderRadius: 1
                                                        }}
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography 
                                                    variant="body2" 
                                                    color="primary"
                                                    component="a"
                                                    href={code.code_url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{ 
                                                        textDecoration: 'none',
                                                        '&:hover': {
                                                            textDecoration: 'underline'
                                                        }
                                                    }}
                                                >
                                                    {code.code_url}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {new Date(code.valid_from).toLocaleDateString()}
                                            </TableCell>
                                            <TableCell>
                                                {code.valid_until ? 
                                                    new Date(code.valid_until).toLocaleDateString() : 
                                                    'No expiration'
                                                }
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>
            </Grid>

            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Confirm Code Owner Deletion
                </DialogTitle>
                <DialogContent>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            <Typography color="error" paragraph>
                                Warning: This action will permanently delete the following records:
                            </Typography>
                            {deletionInfo && (
                                <List>
                                    {deletionInfo.owner_email && (
                                        <ListItem>
                                            <ListItemText
                                                primary="Email Records"
                                                secondary={`${deletionInfo.owner_email.length} records from owner_email table`}
                                            />
                                        </ListItem>
                                    )}
                                    <Divider />
                                    {deletionInfo.email_events && (
                                        <ListItem>
                                            <ListItemText
                                                primary="Email Events"
                                                secondary={`${deletionInfo.email_events.length} events from email_events table`}
                                            />
                                        </ListItem>
                                    )}
                                    <Divider />
                                    {deletionInfo.codes && (
                                        <ListItem>
                                            <ListItemText
                                                primary="Codes"
                                                secondary={
                                                    <Box>
                                                        <Typography variant="body2">
                                                            {deletionInfo.codes.length} codes will be deleted:
                                                        </Typography>
                                                        {deletionInfo.codes.map(code => (
                                                            <Typography key={code.code_id} variant="body2" color="textSecondary">
                                                                • {code.code_value}
                                                            </Typography>
                                                        ))}
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                    )}
                                    <Divider />
                                    {deletionInfo.code_history && (
                                        <ListItem>
                                            <ListItemText
                                                primary="Code History"
                                                secondary={`${deletionInfo.code_history.length} records from code_history table`}
                                            />
                                        </ListItem>
                                    )}
                                    <Divider />
                                    {deletionInfo.owner_candidates && (
                                        <ListItem>
                                            <ListItemText
                                                primary="Owner Candidates"
                                                secondary={`${deletionInfo.owner_candidates.length} records from owner_candidates table`}
                                            />
                                        </ListItem>
                                    )}
                                </List>
                            )}
                            {error && (
                                <Typography color="error" sx={{ mt: 2 }}>
                                    {error}
                                </Typography>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setDeleteDialogOpen(false)}
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        color="error"
                        variant="contained"
                        disabled={loading}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CodeOwnerDetails;